(ns arthur.ui.menu
  "A labelled drop of commands, for a pane head that has more commands than room.

  THE POINT IS NOT SPACE, IT IS THE EXPLANATION. A command that applies only to
  a selected cel spends most of its life disabled, and a disabled button is a
  grey word with its reason hidden in a `title` nobody hovers. The same command
  as a menu row gets a second line saying what it does, visible at the moment
  somebody is looking for it, which is the moment the menu is open.

  So a menu stays openable even when every row in it is disabled, and says what
  is missing instead. Hiding the rows would answer \"where did split go\" with
  silence.

  IT IS PLACED IN THE VIEWPORT, NOT IN THE PANE. A pane clips its own overflow —
  `.time` must, or the timeline would push the window taller — so a panel laid
  out inside one is cut off at its edge. Measured once per opening against the
  button it hangs under, which is also what lets it flip above the button when
  there is more room there. The measurement is of the BUTTON and not of the
  panel, so it does not need the panel to exist yet.

  The scrim is `ui/openmenu`'s, for the reason given there: a catcher behind the
  panel dismisses on any outside click without a document listener that has to
  be added, removed, and told to ignore the click that opened the menu."
  (:require [reagent.core :as r]))

(def ^:private gap 2)
(def ^:private margin 8)

(defn- placement
  "Where to put the panel, given the button it belongs to. `:bottom` rather than
  `:top` when it opens upward, so the panel grows away from the button in both
  directions without anything measuring the panel."
  [^js button]
  (let [r  (.getBoundingClientRect button)
        vh (.-innerHeight js/window)
        vw (.-innerWidth js/window)
        below (- vh (.-bottom r) gap margin)
        above (- (.-top r) gap margin)
        up?   (> above below)]
    (merge {:left (max margin (min (.-left r) (- vw 280 margin)))
            :max-height (max 120 (if up? above below))}
           (if up?
             {:bottom (+ (- vh (.-top r)) gap)}
             {:top (+ (.-bottom r) gap)}))))

(defn view
  "`{:label :title :note :items}`. An item is
  `{:label :sub :disabled? :on-click}`; a nil item is dropped, so a caller can
  put a row behind a `when`. `note` is shown when nothing in the menu applies."
  [_]
  (let [at (r/atom nil)]
    (fn [{:keys [label title note items]}]
      (let [items (remove nil? items)
            dead? (every? :disabled? items)
            close! #(reset! at nil)]
        [:div.menu-wrap
         [:button {:class (when @at "on") :title title
                   :aria-haspopup "menu" :aria-expanded (boolean @at)
                   :on-click (fn [^js e]
                               (if @at (close!) (reset! at (placement (.-currentTarget e)))))}
          label " ▾"]
         (when-let [{:keys [left top bottom max-height]} @at]
           [:<>
            [:div.menu-scrim {:on-click close!}]
            [:div.menu.menu-drop
             {:role "menu"
              :style (cond-> {:left (str left "px") :max-height (str max-height "px")}
                       top    (assoc :top (str top "px"))
                       bottom (assoc :bottom (str bottom "px")))}
             (when (and dead? note) [:div.dim.menu-note note])
             (doall
              (for [{:keys [label sub disabled? on-click]} items]
                ^{:key label}
                ;; The row's accessible name is the COMMAND, not the command
                ;; followed by its explanation — which is what `textContent` is
                ;; here, and what a screen reader would otherwise read out.
                [:button.menu-item {:role "menuitem" :disabled (boolean disabled?)
                                    :aria-label label :title sub
                                    :on-click (fn [] (close!) (on-click))}
                 label
                 (when sub [:span.sub sub])]))]])]))))

(defn popover
  "A button that opens a panel of settings rather than a list of commands: the
  onion's counts, a slider, the palette's name. Placed and dismissed exactly as
  `view` is — measured against its button, shut by a click anywhere else — so
  every popover in the window closes the same way. `body` is a function of the
  close function, for a panel with a control that should shut it."
  [_ _]
  (let [at (r/atom nil)]
    (fn [{:keys [label title class]} body]
      (let [close! #(reset! at nil)]
        [:div.menu-wrap {:class class}
         [:button {:class (str "pop" (when @at " on")) :title title :aria-label title
                   :aria-haspopup "dialog" :aria-expanded (boolean @at)
                   :on-click (fn [^js e]
                               (if @at (close!) (reset! at (placement (.-currentTarget e)))))}
          label]
         (when-let [{:keys [left top bottom max-height]} @at]
           [:<>
            [:div.menu-scrim {:on-click close!}]
            [:div.menu.menu-drop.popout
             {:role "dialog" :aria-label title
              :style (cond-> {:left (str left "px") :max-height (str max-height "px")}
                       top    (assoc :top (str top "px"))
                       bottom (assoc :bottom (str bottom "px")))}
             (body close!)]])]))))
